import apiClient from './api.service';

/** ApiService returns axios JSON body: { success, data } or legacy { status, data }. */
function unwrapData<T>(body: unknown): T {
  if (body == null || typeof body !== 'object') return body as T;
  const b = body as Record<string, unknown>;
  if ('data' in b && (b.success === true || b.status === 'success')) return b.data as T;
  return body as T;
}

export interface Currency {
  id: string;
  company_id: string;
  currency_code: string;
  /** Some UIs use `code`; maps to currency_code when present from API */
  code?: string;
  currency_name: string;
  symbol?: string;
  is_base_currency: boolean;
  exchange_rate: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

class CurrencyService {
  async getCurrencies(): Promise<Currency[]> {
    try {
      const response = await apiClient.get('/finance/currencies');
      console.log('[CurrencyService] getCurrencies response:', response);
      console.log('[CurrencyService] response.data:', response.data);
      
      // Handle different response structures
      let currencies: Currency[] = [];
      
      if (Array.isArray(response.data)) {
        currencies = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        currencies = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        currencies = response.data.data;
      }
      
      console.log('[CurrencyService] Loaded currencies:', currencies.length);
      if (currencies.length > 0) {
        console.log('[CurrencyService] Sample currency:', currencies[0]);
      } else {
        console.warn('[CurrencyService] No currencies found in response');
      }
      
      return currencies;
    } catch (error: any) {
      console.error('[CurrencyService] Error fetching currencies:', error);
      console.error('[CurrencyService] Error response:', error.response?.data);
      // Return empty array instead of throwing to prevent breaking the app
      return [];
    }
  }

  async getCurrencyById(id: string): Promise<Currency> {
    const response = await apiClient.get(`/finance/currencies/${id}`);
    return unwrapData<Currency>(response);
  }

  async createCurrency(data: Partial<Currency>): Promise<Currency> {
    const response = await apiClient.post('/finance/currencies', data);
    return unwrapData<Currency>(response);
  }

  async updateCurrency(id: string, data: Partial<Currency>): Promise<Currency> {
    const response = await apiClient.put(`/finance/currencies/${id}`, data);
    return unwrapData<Currency>(response);
  }

  async deleteCurrency(id: string): Promise<void> {
    await apiClient.delete(`/finance/currencies/${id}`);
  }

  async setBaseCurrency(id: string): Promise<Currency> {
    const response = await apiClient.post(`/finance/currencies/${id}/set-base`);
    return unwrapData<Currency>(response);
  }

  async getExchangeRate(from: string, to: string): Promise<{ from: string; to: string; rate: number }> {
    const response = await apiClient.get('/finance/currencies/exchange-rate', {
      params: { from, to },
    });
    return unwrapData<{ from: string; to: string; rate: number }>(response);
  }
}

export const currencyService = new CurrencyService();

